import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { formatDateUS, parseDateUS, getTodayDate } from '../utils/dateUtils';
import DatePicker from './DatePicker';
import './USDateInput.css';

const USDateInput = forwardRef(({ value, onChange, placeholder = 'MM/DD/YYYY', max, min, required, className = '', id, name, disabled = false, onDisabledClick }, ref) => {
  const [displayValue, setDisplayValue] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0, right: 'auto' });
  const containerRef = useRef(null);
  
  // Default max to today if not provided to prevent future dates
  const effectiveMax = max !== undefined ? max : getTodayDate();

  useEffect(() => {
    if (value) {
      // If value is in YYYY-MM-DD format, convert to MM/DD/YYYY for display
      if (value.includes('-') && value.length === 10) {
        setDisplayValue(formatDateUS(value));
      } else if (value.includes('/')) {
        // Already in US format
        setDisplayValue(value);
      } else {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Calculate calendar position
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const calendarHeight = 330; // Approximate height of calendar (reduced for better fit)
        const calendarWidth = 280; // Min width of calendar
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // Position above ONLY if there's not enough space below (less than calendar height)
        // AND there's more space above than below
        // Otherwise, position below (default)
        const shouldPositionAbove = spaceBelow < calendarHeight && spaceAbove > spaceBelow;
        
        setPositionAbove(shouldPositionAbove);
        
        // Calculate position for fixed positioning
        // Align calendar with the left edge of the input field
        let leftPosition = rect.left;
        
        // If calendar would go off the right edge of screen, align it to the right edge instead
        if (leftPosition + calendarWidth > window.innerWidth) {
          leftPosition = window.innerWidth - calendarWidth - 16; // 16px padding from edge
        }
        
        // Ensure calendar doesn't go off the left edge
        if (leftPosition < 16) {
          leftPosition = 16;
        }
        
        const position = {
          top: shouldPositionAbove ? rect.top - calendarHeight : rect.bottom + 8,
          left: leftPosition,
          right: 'auto'
        };
        
        setCalendarPosition(position);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const handleTextChange = (e) => {
    let inputValue = e.target.value;
    
    // Remove non-numeric and non-slash characters
    inputValue = inputValue.replace(/[^\d/]/g, '');
    
    // Auto-format as user types
    if (inputValue.length <= 2) {
      setDisplayValue(inputValue);
    } else if (inputValue.length <= 5) {
      // Add first slash
      if (inputValue.length === 3 && !inputValue.includes('/')) {
        inputValue = inputValue.slice(0, 2) + '/' + inputValue.slice(2);
      }
      setDisplayValue(inputValue);
    } else if (inputValue.length <= 10) {
      // Add second slash
      if (inputValue.length === 6 && inputValue.split('/').length === 2) {
        inputValue = inputValue.slice(0, 5) + '/' + inputValue.slice(5);
      }
      setDisplayValue(inputValue);
    } else {
      // Limit to 10 characters (MM/DD/YYYY)
      setDisplayValue(inputValue.slice(0, 10));
    }

    // Convert to YYYY-MM-DD format for onChange
    if (inputValue.length === 10) {
      const apiDate = parseDateUS(inputValue);
      if (apiDate) {
        onChange({ target: { name, value: apiDate } });
      }
    } else if (inputValue.length === 0) {
      onChange({ target: { name, value: '' } });
    }
  };

  const handleTextBlur = (e) => {
    const inputValue = e.target.value;
    if (inputValue && inputValue.length === 10) {
      const apiDate = parseDateUS(inputValue);
      if (apiDate) {
        setDisplayValue(formatDateUS(apiDate));
        onChange({ target: { name, value: apiDate } });
      }
    }
  };

  const handleDateSelect = (dateStr) => {
    setDisplayValue(formatDateUS(dateStr));
    onChange({ target: { name, value: dateStr } });
    setShowCalendar(false);
  };

  const openCalendar = () => {
    setShowCalendar(true);
  };

  // Expose method to parent via ref
  useImperativeHandle(ref, () => ({
    openCalendar
  }));

  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) {
      if (onDisabledClick) {
        onDisabledClick();
      }
      return;
    }
    
    setShowCalendar(!showCalendar);
  };

  const handleInputClick = (e) => {
    if (disabled) {
      e.preventDefault();
      if (onDisabledClick) {
        onDisabledClick();
      }
    }
  };

  return (
    <div ref={containerRef} className={`us-date-input-container ${className} ${disabled ? 'disabled' : ''}`}>
      <div className="us-date-input-wrapper">
        <input
          type="text"
          value={displayValue}
          onChange={disabled ? undefined : handleTextChange}
          onBlur={disabled ? undefined : handleTextBlur}
          onClick={handleInputClick}
          placeholder={placeholder}
          maxLength={10}
          className={`us-date-text-input ${className.includes('error') ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
          id={id}
          name={name}
          required={required}
          disabled={disabled}
          readOnly={disabled}
        />
        <button
          type="button"
          className={`us-date-calendar-btn ${disabled ? 'disabled' : ''}`}
          onClick={handleCalendarClick}
          tabIndex={-1}
          disabled={disabled}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M6 2V4M14 2V4M3 8H17M4 4H16C16.5523 4 17 4.44772 17 5V16C17 16.5523 16.5523 17 16 17H4C3.44772 17 3 16.5523 3 16V5C3 4.44772 3.44772 4 4 4Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {showCalendar && !disabled && (
        <DatePicker
          selectedDate={value || ''}
          onSelect={handleDateSelect}
          onClose={() => setShowCalendar(false)}
          min={min}
          max={effectiveMax}
          positionAbove={positionAbove}
          position={calendarPosition}
        />
      )}
    </div>
  );
});

USDateInput.displayName = 'USDateInput';

export default USDateInput;
